import {Alert, Skeleton, Tag} from 'antd';
import {
  CopyOutlined,
  ExportOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import {
  UNTITLED,
  channelLabel,
  formatDateTime,
  languageLabel,
  statusMeta,
} from './utils';
import styles from './ContentTransformations.module.scss';

const copy = (value, label) => {
  if (!value) return;
  navigator.clipboard
    .writeText(value)
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.error(`Could not copy ${label.toLowerCase()}`));
};

const Stat = ({label, children}) => (
  <div className={styles.statCard}>
    <span className={styles.statLabel}>{label}</span>
    <span className={styles.statValue}>{children}</span>
  </div>
);

const MonoRow = ({label, value, copyable}) => (
  <div className={styles.monoRow}>
    <span className={styles.statLabel}>{label}</span>
    {value ? (
      <span className={styles.mono}>
        {value}
        {copyable ? (
          <CopyOutlined
            className={styles.copyIcon}
            title={`Copy ${label.toLowerCase()}`}
            onClick={() => copy(value, label)}
          />
        ) : null}
      </span>
    ) : (
      <span className={styles.muted}>—</span>
    )}
  </div>
);

const DetailsContent = ({record, loading}) => {
  if (loading && !record) return <Skeleton active paragraph={{rows: 8}} />;
  if (!record) return null;

  const status = statusMeta(record.status);

  return (
    <div className={styles.modalRoot}>
      <div className={styles.modalHeader}>
        <span className={styles.modalIcon}>
          <FileTextOutlined />
        </span>
        <div className={styles.detailsHeading}>
          <h2>{record.title || UNTITLED}</h2>
          <Tag color={status.color}>{status.label}</Tag>
        </div>
      </div>

      <div className={styles.modalBody}>
        <div className={styles.statGrid}>
          <Stat label="Language">{languageLabel(record.language)}</Stat>
          <Stat label="Created">{formatDateTime(record.createdAt)}</Stat>
          <Stat label="Last updated">{formatDateTime(record.updatedAt)}</Stat>
        </div>

        <section className={styles.detailSection}>
          <h4>Channels</h4>
          {record.channels?.length ? (
            <div className={styles.chipRow}>
              {record.channels.map(channel => (
                <span key={channel} className={styles.chip}>
                  {channelLabel(channel)}
                </span>
              ))}
            </div>
          ) : (
            <span className={styles.muted}>—</span>
          )}
        </section>

        {/* This is what the admin submitted — the API does not expose the
            generated per-channel output, so it is never labelled that. */}
        <section className={styles.detailSection}>
          <h4>Source content</h4>
          <div className={styles.sourceContent}>{record.content || '—'}</div>
          {record.sourceUrl ? (
            <a
              className={styles.sourceLink}
              href={record.sourceUrl}
              target="_blank"
              rel="noopener noreferrer">
              {record.sourceUrl} <ExportOutlined />
            </a>
          ) : null}
        </section>

        <section className={styles.detailSection}>
          <h4>Workflow</h4>
          <div className={styles.monoBlock}>
            <MonoRow label="Request ID" value={record.requestId} copyable />
            <MonoRow label="Record ID" value={record.id} copyable />
            <MonoRow label="Execution ID" value={record.executionId} />
            <div className={styles.monoRow}>
              <span className={styles.statLabel}>Execution</span>
              {record.executionUrl ? (
                <a
                  href={record.executionUrl}
                  target="_blank"
                  rel="noopener noreferrer">
                  Open n8n execution <ExportOutlined />
                </a>
              ) : (
                <span className={styles.muted}>—</span>
              )}
            </div>
          </div>
        </section>

        {record.error ? (
          <Alert
            type="error"
            showIcon
            message="Workflow error"
            description={
              <span className={styles.errorText}>{record.error}</span>
            }
          />
        ) : null}
      </div>
    </div>
  );
};

export const TransformationDetailsModal = ({
  open,
  record,
  loading,
  onClose,
}) => (
  <DBModal
    isOpen={open}
    width={720}
    destroyOnClose
    handleClose={onClose}
    content={<DetailsContent record={record} loading={loading} />}
  />
);
